import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Observable, catchError, from, switchMap, throwError } from "rxjs";
import { AppConfig } from "../../app.config";
import {
  AuthSessionService,
  RetryableRefreshError,
  SessionExpiredError,
} from "../../services/auth/auth-session.service";

const REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);
/** 期限切れのトークンを一時的に取り直せなかったときの error_code(503) */
export const TOKEN_REFRESH_UNAVAILABLE = "token_refresh_unavailable";

/**
 * 新方式のアクセストークン(30分)の期限切れを、利用者に見せずに取り直す。pato-escalation-db#1296
 * - 送信前: 期限が近ければ先に取り直してから送る(期限は端末の時計での受け取り時刻から数える)
 * - 応答が 401 token_expired: 取り直して1回だけ送り直す。取り直せなければ元の 401 を返す(既存の 401 の扱いに任せる)
 * - 旧トークン(期限なし)のリクエストには何もしない
 */
@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.isTarget(req.url)) return next.handle(req);

    // AuthSessionService は Api(HttpClient)に依存するため、コンストラクタで受けると循環依存になる。使う時に取り出す
    const session = this.injector.get(AuthSessionService);
    const token = req.headers.get(AppConfig.AUTH_TOKEN_KEY);
    const shouldRefresh = session.shouldRefreshSoon(token);

    const send = (request: HttpRequest<unknown>) =>
      next.handle(request).pipe(
        catchError((error: unknown) => {
          if (
            !(error instanceof HttpErrorResponse) ||
            error.status !== 401 ||
            error.error?.error_code !== "token_expired" ||
            request.context.get(REFRESH_RETRIED)
          ) {
            return throwError(() => error);
          }
          return this.retryWithRefresh(request, next, session, error);
        })
      );

    if (!shouldRefresh) return send(req);
    return from(session.refresh()).pipe(
      switchMap((newToken) =>
        send(
          req.clone({ setHeaders: { [AppConfig.AUTH_TOKEN_KEY]: newToken } })
        )
      ),
      catchError((error: unknown) => {
        // 送信前の取り直しに失敗しても、元のトークンのまま送って通常の応答・エラー処理に任せる
        if (
          error instanceof RetryableRefreshError ||
          error instanceof SessionExpiredError
        ) {
          return send(req);
        }
        return throwError(() => error);
      })
    );
  }

  private retryWithRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    session: AuthSessionService,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return from(
      session.refresh(req.headers.get(AppConfig.AUTH_TOKEN_KEY))
    ).pipe(
      switchMap((token) =>
        next.handle(
          req.clone({
            context: req.context.set(REFRESH_RETRIED, true),
            setHeaders: { [AppConfig.AUTH_TOKEN_KEY]: token },
          })
        )
      ),
      catchError((error: unknown) => {
        // リフレッシュトークンが失効していた: 元の 401 を返し、既存の 401 処理(ログアウト)に任せる
        if (error instanceof SessionExpiredError) {
          return throwError(() => originalError);
        }
        // 一時的に取り直せなかった(混雑・サーバー障害・通信エラー): 401 のまま返すと起動時の /me などで
        // トークンを消してログアウトさせてしまうため、再試行できるエラー(503)にする
        if (error instanceof RetryableRefreshError) {
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 503,
                statusText: "Token refresh unavailable",
                url: req.url,
                error: { error_code: TOKEN_REFRESH_UNAVAILABLE },
              })
          );
        }
        return throwError(() => error);
      })
    );
  }

  private isTarget(url: string): boolean {
    if (!url.startsWith(AppConfig.API_URL_BASE)) return false;
    return !["auth/refresh", "auth/upgrade_token", "auth/sign_out"].some(
      (path) => url.includes(`/` + path)
    );
  }
}
